import React, {useState} from "react";
import { NO_SEARCH_RESULTS, TABLE_HEADERS } from "@/utils/constants/appConstants/commonConstants";
import { Link, useNavigate } from "react-router-dom";
import { DateFormatter, TimeFormatter, concatenateName, getStatusClass } from "@/utils/functions/helperFunctions";
import StatusBadge from "@/components/badges/StatusBadge";
import { ReportedEnvelopeTableSkeletonDesktop, ReportedEnvelopeTableSkeletonMobile } from "@/components/skeletons";
import { useCompactListLayout } from "@/utils/hooks/useCompactListLayout";
import NoDataAvailable from '@/components/cards/NoDataAvailable';
import NoFilterDataCard from '@/components/cards/NoFilterDataCard';
import { MobileDateField } from "@/components/cards/MobileDateField";
import { MobileListChevron } from "@/components/cards/MobileListChevron";

interface ReportedEnvelopeTableProps {
    envelopes: [];
    setEnvelopes: (envelope: any) => void;
    isLoading: boolean;
    dataLength: number;
    sorting: string;
    allFetched?: boolean;
    setSorting: ({}) => void;
    search: (searchText: string) => void;
    reset: () => void;
    searchItem: string | null;
    title: string;
    dataLoad: boolean;
    resetDataQueries: () => void;
    setPageNo: (pageNo: number) => void;
    id: string;
    isReported?: boolean;
}

const ReportedEnvelopeTable: React.FC<ReportedEnvelopeTableProps> = (props) => {
    const navigate = useNavigate();
    const isCompactLayout = useCompactListLayout();
    const [subjectStatus, setSubjectStatus] = useState(true);
    const [createdAtStatus, setCreatedAtStatus] = useState(true);
    const [sortElement, setSortElement] = React.useState("created_at");

    const subjectSort = () => {
        setSubjectStatus(!subjectStatus);
        setSortElement('subject');
        props.setPageNo(1)
        props.setSorting({'meta_data.email_subject': !subjectStatus ? 1 : -1 })
    }

    const createdAtSort = () => {
        setCreatedAtStatus((prevStatus) => !prevStatus);
        setSortElement('created_at');
        props.setPageNo(1);
        props.setSorting({ 'created_at': createdAtStatus ? 1 : -1 });
    };
    return isCompactLayout ? (
        props.envelopes && !props.dataLoad ? (
            props.envelopes?.length > 0 ? (
                <div className="envelope_table">
                    {props.envelopes?.map((envelope: any, key: number) => (
                        <div className="envelope_tr gs-mobile-list-card" key={envelope.id} id={`${props.id}-envelope-${key}`} onClick={() => navigate(`/reported-envelope/${envelope?.id}`)}>
                            <div className="gs-mobile-card-icon">
                                <em className="icon ni ni-file icons" id={`${props.id}-icon`}></em>
                            </div>
                            <div className="env_meta_data gs-mobile-list-card__content w-100" id={`${props.id}-subject-${key}-mb`}>
                                {envelope?.meta_data?.email_subject}
                                <div className="text-soft" id={`${props.id}-recipient-name-${key}-mb`}>
                                    To:{" "}
                                    {envelope.recipients.length === 0
                                        ? "No Recipients Specified"
                                        : envelope.recipients
                                              .map((v: any) => v.full_name)
                                              .slice(0, 3)
                                              .join(", ")}
                                </div>
                                <div className="text-dark" id={`${props.id}-report-reason-${key}-mb`}>
                                    {envelope.created_by?.history?.filter((history: any) => history.type === "reported" && history?.data?.envelope === envelope.id).map((history: any) => history?.reason).join(", ")}
                                </div>
                                <div className="text-dark gs-envelope-mobile-status-field">
                                    <span className="fw-bold">{TABLE_HEADERS.STATUS}: </span>
                                    <span className="gs-envelope-mobile-status-value" id={`${props.id}-status-${key}-mb`}>
                                        <StatusBadge envelope={envelope} id={`${key}-${props.id}`} />
                                    </span>
                                </div>
                                <MobileDateField
                                    label="Created At: "
                                    value={envelope.created_at}
                                    dateId={`${props.id}-created-at-date-${key}-mb`}
                                    timeId={`${props.id}-created-at-time-${key}-mb`}
                                />
                            </div>
                            <MobileListChevron
                                id={`${props.id}-redirect-${key}-mb`}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    navigate(`/reported-envelope/${envelope?.id}`);
                                }}
                            />
                        </div>
                    ))}
                </div>
            ) : props.searchItem ? (
                <NoDataAvailable><p id={`${props.id}-no-search-result`}>{NO_SEARCH_RESULTS}</p></NoDataAvailable>
            ) : (
                <NoDataAvailable>
                    <NoFilterDataCard id={props.id} loadAll={props.resetDataQueries} />
                </NoDataAvailable>
            )
        ) : (
            <ReportedEnvelopeTableSkeletonMobile />
        )
    ) : (
        <table className="nk-tb-list nk-tb-ulist table-weight" style={{ tableLayout: "fixed", width: "100%" }}>
            <thead className="">
                <tr className="nk-tb-item nk-tb-head">
                    <th className="nk-tb-col w-35" onClick={subjectSort}>
                        <span id={`${props.id}-subject-th`}>{TABLE_HEADERS.SUBJECT}</span>
                        <em id={`${props.id}-subject-th-sort-icon`} className={`ml-1  icon ni ${sortElement === 'subject' ? (subjectStatus === true ? "ni-upword-alt-fill" : "ni-downward-alt-fill") : ""}`}></em>
                    </th>
                    <th className="nk-tb-col w-30">
                        <span id={`${props.id}-report-reason-th`}>{TABLE_HEADERS.REPORT_REASON}</span>
                    </th>
                    <th className="nk-tb-col w-15">
                        <span id={`${props.id}-status-th`}>{TABLE_HEADERS.STATUS}</span>
                    </th>
                    <th className="nk-tb-col w-15">
                        <span id={`${props.id}-reported-at-th`}>{TABLE_HEADERS.REPORTED_AT}</span>
                    </th>
                    <th className="nk-tb-col w-15" onClick={createdAtSort}>
                        <span id={`${props.id}-created-at-th`}>{TABLE_HEADERS.CREATED_AT}</span>
                        <em id={`${props.id}-created-at-th-sort-icon`} className={`ml-1  icon ni ${sortElement === 'created_at' ? (createdAtStatus === true ? "ni-upword-alt-fill" : "ni-downward-alt-fill") : ''}`}></em>
                    </th>
                </tr>
            </thead>
            {props.envelopes && !props.dataLoad ? (
                props.envelopes?.length > 0 ? (
                    <tbody>
                        {props.envelopes.map((envelope: any, key: number) => (
                            <tr className="nk-tb-item" key={envelope.id}>
                                <td className="nk-tb-col cursor-pointer">
                                    <Link to={`/reported-envelope/${envelope?.id}`} className="user-card">
                                        <div className="nk-tnx-type w-100">
                                            <div className="bg-fail-dim icon mr-2">
                                                <em className="icon ni ni-file icons" id={`${props.id}-icon-${key}`}></em>
                                            </div>
                                            <div
                                                className="data"
                                                style={{
                                                    marginTop: 4,
                                                    maxWidth: "100%",
                                                    overflow: "hidden",
                                                    whiteSpace: "nowrap",
                                                    textOverflow: "ellipsis",
                                                }}
                                            >
                                                <div className="info title-overflow">
                                                    <div id={`${props.id}-subject-${key}`} className="text-dark name font-weight-bold text-wrap">{envelope.meta_data?.email_subject ? envelope.meta_data?.email_subject : "Untitled"}</div>
                                                    <div className="meta" style={{ marginTop: 4 }}>
                                                        <span className="version">
                                                            <span className="text-soft" id={`${props.id}-recipient-name-${key}`}>
                                                                To:{" "}
                                                                {envelope.recipients.length === 0
                                                                    ? "No Recipients Specified"
                                                                    : envelope.recipients
                                                                          .map((v: any) => v.full_name)
                                                                          .slice(0, 3)
                                                                          .join(", ")}
                                                            </span>
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </Link>
                                </td>
                                <td className="nk-tb-col">
                                    <span className="tb-amount" id={`${props.id}-report-reason-${key}`}>{envelope.created_by?.history?.filter((history: any) => history.type === "reported" && history?.data?.envelope === envelope.id).map((history: any) => history?.reason).join(", ")}</span>
                                </td>
                                <td className="nk-tb-col">
                                    <StatusBadge envelope={envelope} id={`${key}-${props.id}`} />
                                </td>
                                <td className="nk-tb-col">
                                    <span className="text-dark" id={`${props.id}-created-at-date-${key}`}>{DateFormatter(envelope.created_by?.history?.find((history: any) => history.type === "reported" && history?.data?.envelope === envelope.id)?.flagged_at)}</span>
                                    <div>
                                        <div className="meta mt-1">
                                            <span className="version">
                                                <span className="text-soft" id={`${props.id}-created-at-time-${key}`}>{TimeFormatter(envelope.created_by?.history?.find((history: any) => history.type === "reported" && history?.data?.envelope === envelope.id)?.flagged_at)}</span>
                                            </span>
                                        </div>
                                    </div>
                                </td>
                                <td className="nk-tb-col">
                                    <span className="text-dark" id={`${props.id}-created-at-date-${key}`}>{DateFormatter(envelope.created_at)}</span>
                                    <div>
                                        <div className="meta mt-1">
                                            <span className="version">
                                                <span className="text-soft" id={`${props.id}-created-at-time-${key}`}>{TimeFormatter(envelope.created_at)}</span>
                                            </span>
                                        </div>
                                    </div>
                                </td>
                            </tr>
                        ))}
                        {props.isLoading && <ReportedEnvelopeTableSkeletonDesktop rowCount={2} />}
                    </tbody>
                ) : props.searchItem ? (
                    <tbody>
                        <tr>
                            <td colSpan={4}>
                                <NoDataAvailable><p id={`${props.id}-no-search-result`}>{NO_SEARCH_RESULTS}</p></NoDataAvailable>
                            </td>
                        </tr>
                    </tbody>
                ) : (
                    <tbody>
                        <tr>
                            <td colSpan={4}>
                                <NoDataAvailable>
                                    <NoFilterDataCard id={props.id} loadAll={props.resetDataQueries} />
                                </NoDataAvailable>
                            </td>
                        </tr>
                    </tbody>
                )
            ) : (
                <tbody>
                    <ReportedEnvelopeTableSkeletonDesktop />
                </tbody>
            )}
        </table>
    );
};

export default ReportedEnvelopeTable;
